import { Platform } from 'react-native';
import { t } from '../i18n';
import { offresActives } from './offres';

import type { FeatureKey, Plan } from '../types';

// ⚠️ ADRESSE DE CONTACT ÉCOLES — modifie UNIQUEMENT cette ligne pour changer le destinataire
// du bouton « Nous contacter » (onglet Écoles du Paywall).
export const SCHOOL_CONTACT_EMAIL = 'info@alternjob.com';

export interface PlanCard {
  key: Plan;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  popular?: boolean;
  /** Ligne sous le prix : l'écart avec l'offre inférieure, lisible d'un coup d'œil. */
  ecart?: string;
  /**
   * Le prix affiché vient-il du fournisseur (SDK) ? `false` = repli du dépôt ou prix absent.
   * Absent sur la carte Free, qui n'affiche pas de montant. Règle 12 : un bouton d'achat ne
   * s'affiche jamais à côté d'un prix qui ne vient pas du fournisseur.
   */
  prixFournisseur?: boolean;
}

const RANG_PLAN: Record<Plan, number> = { free: 0, plus: 1, pro: 2, school: 3 };

/**
 * UN ACHAT N'A DE SENS QUE VERS LE HAUT, ET JAMAIS DEPUIS UN COMPTE ÉCOLE. Le webhook ignore un achat
 * sur un compte `school` (la licence est payée par l'établissement) : l'élève aurait payé pour
 * rien. Un abonné Pro n'a pas non plus à se voir proposer Plus : selon la configuration des groupes
 * d'abonnement des boutiques, ce serait un second abonnement facturé.
 */
export function achatPossible(actuel: Plan, cible: Plan): boolean {
  if (actuel === 'school' || cible === 'free' || cible === 'school') return false;
  return RANG_PLAN[cible] > RANG_PLAN[actuel];
}

/** Prix mensuels servis par le SDK, `null` quand le package n'est pas lisible. */
export interface PrixMensuels {
  plus: string | null;
  pro: string | null;
}

/**
 * REPLI, ET SEULEMENT REPLI. Ces montants ne s'affichent que si le SDK ne rend pas le package :
 * offering d'une version antérieure, réseau coupé, achats indisponibles. Ils PEUVENT DIVERGER du
 * store — ils sont en euros, figés, et ne suivent ni une hausse de tarif ni la devise de l'acheteur.
 *
 * Grille 1.1.0 (décision du 09/10/2026) : Plus 11,99 €, Pro 19,99 € — à régler dans les deux
 * consoles AVANT la sortie ; jusque-là, le store sert encore 7,99 € et 14,99 €, et c'est lui qui
 * s'affiche. Les abonnés en cours gardent leur ancien prix (réglé dans les stores) : rien dans l'app
 * ne lit ce repli comme le prix payé par quelqu'un, il ne sert qu'à la carte d'un paywall muet.
 *
 * Jamais d'arrondi (« 12€ », « 20€ ») : une carte arrondie affiche un montant que personne ne paie.
 * Un repli a le droit d'être périmé, pas d'être faux.
 */
const PRIX_REPLI: PrixMensuels = { plus: '11,99 €', pro: '19,99 €' };

/**
 * LE REPLI NE S'APPLIQUE PAS SUR LE WEB, ET CE N'EST PAS UNE INCOHÉRENCE.
 *
 * Sur mobile, il existe une raison de replier : le SDK peut être momentanément muet — offering
 * d'une version antérieure du binaire, réseau coupé — alors qu'un tarif existe bel et bien dans le
 * store. Le repli est périmé, il n'est pas inventé.
 *
 * Sur le web, aucune de ces raisons ne tient. Les produits web portent leurs prix dans le même
 * offering que celui qu'on interroge : si l'on n'obtient rien, c'est que l'offering est vide,
 * mal apparié ou illisible — trois défauts que `fetchTarifs` remonte déjà en incident. Afficher
 * `7,99 €` par-dessus reviendrait à recouvrir le signal par une valeur du dépôt, et à présenter
 * comme un tarif un montant que personne n'a confronté au catalogue.
 *
 * UN PRIX FAUX EST PIRE QU'UN PRIX ABSENT : l'un fait payer autre chose que ce qui est annoncé,
 * l'autre fait poser une question. Sur le web, on préfère la question.
 */
function repli(): PrixMensuels {
  // Lu À L'APPEL et non figé à l'import : une constante de module gèlerait la plateforme au premier
  // chargement — sans conséquence en production, où elle ne change pas, mais elle rend la règle
  // invérifiable par un test, et une règle sur de l'argent qu'on ne peut pas éprouver n'en est pas
  // une. Même raison que pour les libellés traduits construits au rendu, plus bas.
  return Platform.OS === 'web' ? { plus: null, pro: null } : PRIX_REPLI;
}

/**
 * Offres individuelles. Construites AU RENDU : une constante de module figerait les libellés dans
 * la langue chargée au premier import.
 *
 * LES PRIX VIENNENT DU SDK, plus du code. Un montant écrit ici serait faux pour quiconque paie en
 * dollars ou en rands, et faux aussi en euros dès la première modification de tarif dans les
 * consoles — le store resterait la vérité, la carte mentirait. `priceString` est déjà formaté par
 * RevenueCat dans la devise et la locale de l'acheteur.
 *
 * NE SE TRADUISENT PAS, et un test le retient : les NOMS d'offre (« Free », « Accélérateur »,
 * « Pro ») sont des noms commerciaux, ils figurent tels quels sur les fiches App Store et les
 * factures.
 *
 * LA CARTE FREE, ELLE, PORTE UN MOT ET NON UN MONTANT — corrigé le 18/09/2026. Elle affichait
 * « 0€ », écrit en dur. Le montant est juste partout, le SYMBOLE ne l'est pas : un acheteur
 * américain voyait « 0€ » sur la même carte où les deux autres prix sortaient du SDK en dollars.
 * Zéro euro et zéro dollar valent la même chose, mais « 0€ » affiché à côté de « $14.99 » dit à
 * l'utilisateur qu'il regarde une grille tarifaire mal traduite. « Gratuit » / « Free » ne relève
 * d'aucune devise, donc d'aucune de ces contradictions.
 */
// Les lignes de l'onglet Offres suivent la garde : le paywall part aussi par mise à jour à distance
// vers les binaires qui gardent l'écran d'attente, et n'y vend pas des candidatures inutilisables.
// Entretiens puis candidatures EN TÊTE : ce sont les deux plafonds qui distinguent les offres
// payantes (8 contre 20, 15 contre 60), ils doivent se lire à la même hauteur d'une carte à l'autre.
// La vraie voix se compte par semaine en Plus et Pro (le Free l'a sur son seul entretien du mois,
// déjà dit par sa ligne d'entretiens) : sa ligne suit celle des entretiens.
const lignes = (plan: 'free' | 'plus' | 'pro', offres: boolean): string[] => [
  t(`plans.${plan}.entretiens`),
  ...(plan === 'free' ? [] : [t(`plans.${plan}.voix`)]),
  ...(offres ? (t(`plans.${plan}.featuresOffres`) as unknown as string[]) : []),
  ...(t(`plans.${plan}.features`) as unknown as string[]),
];

export const individualPlans = (prix: PrixMensuels = { plus: null, pro: null }, offres: boolean = offresActives()): PlanCard[] => [
  {
    key: 'free',
    name: 'Free',
    price: t('plans.free.price'),
    period: t('plans.free.period'),
    tagline: t('plans.free.tagline'),
    features: lignes('free', offres),
  },
  {
    key: 'plus',
    name: 'Accélérateur',
    price: prix.plus ?? repli().plus ?? '',
    prixFournisseur: prix.plus !== null,
    period: t('plans.plus.period'),
    tagline: t('plans.plus.tagline'),
    features: lignes('plus', offres),
  },
  {
    key: 'pro',
    name: 'Pro',
    price: prix.pro ?? repli().pro ?? '',
    prixFournisseur: prix.pro !== null,
    period: t('plans.pro.period'),
    tagline: t('plans.pro.tagline'),
    // Mis en avant depuis la grille 1.1.0 : bandeau « Recommandé », sélectionné par défaut.
    popular: true,
    ecart: t(offres ? 'plans.pro.ecart' : 'plans.pro.ecartSansOffres'),
    features: lignes('pro', offres),
  },
];

export interface SchoolPlan {
  name: string;
  features: string[];
}

// Tarif volontairement absent : l'offre établissement se négocie hors app.
// Tarif volontairement absent : l'offre établissement se négocie hors app.
// « Offre établissement » est un nom commercial, il ne se traduit pas.
export const schoolPlan = (): SchoolPlan => ({
  name: 'Offre établissement',
  features: t('plans.school.features') as unknown as string[],
});

// Plan mis en avant selon la feature à l'origine du blocage (sinon affichage standard).
export function recommendedPlanFor(feature: FeatureKey | null): Plan | null {
  switch (feature) {
    case 'cv_create':
    case 'section_rewrite':
    case 'export':
    case 'linkedin_score':
    case 'cv_score':
    // cv_import a un quota gratuit (1/mois) comme cv_score : quand il est atteint, c'est Plus qui
    // débloque, pas Pro.
    case 'cv_import':
    // Mêmes paliers que cv_score et cv_import — free 1, plus 5, pro illimité : c'est Accélérateur
    // qui débloque quand le quota gratuit est atteint, pas Pro.
    case 'cover_letter':
    case 'cover_letter_analyse':
    // « Améliorer ma lettre » : réservé aux abonnés (Free 0, Plus 5), Plus le débloque.
    case 'cover_letter_improve':
    // Candidatures IA de l'onglet Offres : Free 2, Plus 15 — Plus débloque.
    case 'offre_dossier':
      return 'plus';
    case 'interview_sim':
      return 'pro';
    default:
      return null;
  }
}
